import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, Share2, ChevronRight, ExternalLink, Compass, Layers, CheckCircle } from 'lucide-react';

// Import Shared Components
import { AISnippetBlock, formatQuickAnswer, generateKeyPoints } from '@/components/AISnippetBlock';
import { PeopleAlsoAsk, DEFAULT_IPTV_FAQ } from '@/components/PeopleAlsoAsk';
import { BreadcrumbNav, createBlogBreadcrumbs } from '@/components/BreadcrumbNav';
import { SchemaGenerator } from '@/components/SchemaGenerator';
import { DEEP_LINKING_MAP, getRelatedArticles } from '@/components/BlogArticleContent';
import { getArticleImages, getOptimizedSearchQuery } from '@/services/imageService';
import { getCurrentMonthYear } from '@/services/eliteSummaryGenerator';
import { TableOfContents } from '@/components/TableOfContents';
import { PillarContentRenderer } from '@/components/PillarContentRenderer';
import { loadAllArticles } from '@/services/articleLoader';
import { ArticleTags } from '@/components/ArticleTags';
import { Footer } from '@/components/Footer';
import { ArticleFooter } from '@/components/ArticleFooter';
import '@/styles/eliteDesignSystem.css';

/**
 * GuideLayout - For In-Depth How-To's and Comprehensive Topic Guides
 * 
 * Distinct from PillarLayout (Ultimate Authority) and StandardLayout (News/Blogs).
 */
export const GuideLayout = ({ article }) => {
    if (!article) return null;

    // State
    const [featuredImage, setFeaturedImage] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [allArticles, setAllArticles] = useState([]);

    // Load Data
    useEffect(() => {
        const loadData = async () => {
            try {
                const articles = await loadAllArticles();
                if (articles && articles.length > 0) setAllArticles(articles);
            } catch (e) {
                console.warn('Error loading articles', e);
            }
        };
        loadData();
    }, []);

    // Fetch Image
    useEffect(() => {
        const fetchImage = async () => {
            try {
                setImageLoading(true);
                const searchQuery = getOptimizedSearchQuery(article.title + " guide");
                const images = await getArticleImages(searchQuery, 1);
                if (images.length > 0) setFeaturedImage(images[0]);
            } catch (e) {
                console.error(e);
            } finally {
                setImageLoading(false);
            }
        };
        fetchImage();
    }, [article.title, article.id]);

    // Derived Data
    const quickAnswer = article.quickAnswer || formatQuickAnswer(article);
    const keyPoints = article.keyPoints || generateKeyPoints(article);
    const faqItems = article.faqItems || DEFAULT_IPTV_FAQ;
    const lastUpdated = getCurrentMonthYear();
    const breadcrumbs = createBlogBreadcrumbs(article);

    // Find related content for cluster map
    const deepLinkedRelated = allArticles.length > 0 && getRelatedArticles
        ? getRelatedArticles(article.slug, allArticles)
        : [];

    return (
        <>
            {/* TableOfContents removed - new design doesn't use floating sidebar */}

            <SchemaGenerator
                article={{
                    ...article,
                    datePublished: article.date,
                    dateModified: `2026-01-${String(Math.min(article.id + 10, 31)).padStart(2, '0')}`,
                    keywords: `${article.category}, guide, how to, ${article.title.toLowerCase()}`
                }}
                faqItems={faqItems}
                breadcrumbs={breadcrumbs}
                type="HowTo" // Specialized schema for Guides
            />

            <div className="min-h-screen bg-background">
                {/* HERO SECTION: Guide Style (Blue/Teal Accents) */}
                <section className="relative pt-24 pb-12 px-4 overflow-hidden border-b border-white/5">
                    {/* Background Glow */}
                    <div className="absolute top-0 right-0 w-full h-full max-w-7xl mx-auto overflow-hidden">
                        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-[120px]" />
                    </div>

                    <div className="relative max-w-5xl mx-auto z-10">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <span className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-md text-xs font-bold text-cyan-400 uppercase tracking-wider">
                                    <Compass className="w-3 h-3" /> Comprehensive Guide
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    {article.readTime} Read
                                </span>
                            </div>

                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight text-white leading-[1.1]">
                                {article.title}
                            </h1>

                            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-l-2 border-primary/50 pl-6">
                                <p className="max-w-2xl italic">
                                    "{article.excerpt}"
                                </p>
                                <div className="flex items-center gap-2 ml-auto">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span>Updated {lastUpdated}</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* MAIN CONTENT CONTAINER */}
                <div className="max-w-5xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">

                    {/* MAIN COLUMN */}
                    <main className="lg:col-span-8">
                        {/* 🔍 ELITE SUMMARY BLOCK */}
                        <div className="mb-10">
                            <AISnippetBlock
                                quickAnswer={quickAnswer}
                                keyPoints={keyPoints}
                                lastUpdated={lastUpdated}
                                variant="featured"
                                content={article.content}
                            />
                        </div>

                        {/* FEATURED IMAGE */}
                        {featuredImage && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mb-10 rounded-xl overflow-hidden border border-white/10"
                            >
                                <img
                                    src={featuredImage.url}
                                    alt={article.title}
                                    className="w-full aspect-video object-cover"
                                />
                            </motion.div>
                        )}

                        {/* CONTENT BODY */}
                        <div className="prose prose-invert prose-lg max-w-none prose-headings:text-cyan-50 text-slate-300">
                            <PillarContentRenderer content={article.content} />
                        </div>

                        {/* GUIDE CLUSTER MAP (Different style for Guides) */}
                        {DEEP_LINKING_MAP[article.slug] && (
                            <div className="my-12 p-6 bg-cyan-950/20 border border-cyan-900/50 rounded-lg">
                                <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-cyan-400">
                                    <Layers className="w-5 h-5" /> Related Guides
                                </h3>
                                <div className="space-y-3">
                                    {DEEP_LINKING_MAP[article.slug].map((link, idx) => {
                                        const linkExists = allArticles.find(a => a.slug === link.slug);
                                        if (!linkExists) return null;
                                        return (
                                            <Link
                                                key={idx}
                                                to={`/blog/${link.slug}`}
                                                className="block p-3 bg-black/20 hover:bg-cyan-900/20 border border-transparent hover:border-cyan-500/30 rounded transition"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-slate-200">{link.anchor}</span>
                                                    <ChevronRight className="w-4 h-4 text-cyan-500/50" />
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* PEOPLE ALSO ASK */}
                        <div className="mt-12">
                            <PeopleAlsoAsk faqItems={faqItems} title="Common Questions" variant="default" />
                        </div>

                        {/* ARTICLE TAGS */}
                        {article.tags && article.tags.length > 0 && (
                            <div className="mt-8 pt-6 border-t border-white/10">
                                <ArticleTags tags={article.tags} variant="default" />
                            </div>
                        )}

                        {/* Article Footer - Share & Navigation */}
                        <ArticleFooter
                            title={article.title}
                            nextArticleTitle={deepLinkedRelated[0]?.title || 'Best IPTV USA 2026'}
                            nextArticleUrl={deepLinkedRelated[0] ? `/blog/${deepLinkedRelated[0].slug}` : '/blog/best-iptv-usa-2026'}
                        />
                    </main>

                    {/* SIDEBAR (Desktop Only) */}
                    <aside className="hidden lg:block lg:col-span-4 space-y-8">
                        {/* Sticky Container */}
                        <div className="sticky top-24">
                            <div className="p-6 rounded-xl bg-card border border-white/10">
                                <h3 className="font-bold mb-4 text-sm text-muted-foreground uppercase tracking-wider">In This Guide</h3>
                                {/* We could render a mini TOC here or just CTA */}
                                <nav className="space-y-2 text-sm">
                                    <a href="#quick-answer" className="block text-primary hover:underline">1. Quick Answer</a>
                                    <a href="#overview" className="block text-muted-foreground hover:text-white transition">2. Overview</a>
                                    <a href="#people-also-ask" className="block text-muted-foreground hover:text-white transition">3. FAQ</a>
                                </nav>
                            </div>

                            <div className="mt-6 p-6 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/10 border border-primary/20 text-center">
                                <h4 className="font-bold text-white mb-2">Need Help?</h4>
                                <p className="text-sm text-muted-foreground mb-4">Start your free trial today.</p>
                                <a href="https://xtreamcube.com/freetrialiptv/" className="block w-full py-2 bg-primary text-primary-foreground rounded font-bold hover:bg-primary/90 transition">
                                    Get Started
                                </a>
                            </div>
                        </div>
                    </aside>

                </div>
            </div>
            {/* Footer moved to BlogArticlePage */}
        </>
    );
};
